import { Tab, Tabs, IconButton } from "@mui/material";
import React from "react";
import { useRouteQuery, useUpdateQueryParam } from "../../hooks/router.hooks";
import { Property } from "./Property";
import { useParams } from "react-router-dom";
import { PropertyMarketInsightsRoute } from "./marketInsights/PropertyMarketInsightsRoute";
import { PropertyLocationReport } from "./PropertyLocationReport";
import { PropertyShareButton } from "./PropertyShareButton";
import { PropertyLocationPdfButton } from "./PropertyLocationPdfButton";
import { SimilarPropertiesInsightsRoute } from "./timeOnMarket/SimilarPropertiesInsightsRoute";
import { useAppraiser } from "../../hooks/auth.hooks";
import { AppraiserRole } from "../../types/appraiser.types";
import { LoaderCentered } from "../../components/loaders/LoaderCentered";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useClipboard } from "../../components/clipboard/ClipboardContextProvider";
import { Typography } from "common/components/Typography";
import { ValuationType } from "common/types/common.types";
import { useProperty } from "../../hooks/property.hooks";

interface PropertyRouteProps {}

export const PropertyRoute: React.FC<PropertyRouteProps> = () => {
  const { t } = useTranslation();
  const { copyToClipboard } = useClipboard();

  const tabs = [
    {
      value: "info",
      label: t("propertyRoute.propertyInfo"),
    },
    {
      value: "location",
      label: t("propertyRoute.locationReport"),
    },
    {
      value: "marketInsights",
      label: t("propertyRoute.marketOverview"),
    },
    {
      value: "timeOnMarket",
      label: t("propertyRoute.similarPropertiesOnMarket"),
    },
  ];

  const query = useRouteQuery();

  const { updateQueryParam } = useUpdateQueryParam();

  const { propertyId } = useParams();

  const { data: appraiser, isLoading: isAppraiserLoading } = useAppraiser();
  const { data: property, isLoading: isPropertyLoading } = useProperty(
    propertyId ?? "",
    !!propertyId
  );

  if (!propertyId) {
    return <div>{t("common.pathNotFound")}</div>;
  }

  if (isAppraiserLoading || isPropertyLoading) {
    return <LoaderCentered />;
  }

  if (!appraiser) {
    return null;
  }

  const filteredTabs =
    appraiser?.role === AppraiserRole.DATA_ENTRY
      ? tabs.filter((tab) => tab.value === "info")
      : tabs;

  const defaultSection =
    property?.valuationType === ValuationType.LocationCheck &&
    filteredTabs.some((tab) => tab.value === "location")
      ? "location"
      : "info";
  const section = query.get("section") ?? defaultSection;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" textStyle={{ fontSize: 16 }}>
            {t("propertyDetails.id")} {propertyId}
          </Typography>
          <IconButton onClick={() => copyToClipboard(propertyId)} size="small">
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </div>
        {appraiser?.role !== AppraiserRole.DATA_ENTRY && (
          <PropertyShareButton propertyId={propertyId} />
        )}
      </div>
      <Tabs
        value={section}
        onChange={(_, value) => {
          updateQueryParam("section", value);
        }}
        style={{ marginBottom: "20px" }}
      >
        {filteredTabs.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>
      {appraiser?.role !== AppraiserRole.DATA_ENTRY &&
        property?.valuationType === ValuationType.LocationCheck &&
        section === "location" && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "20px",
            }}
          >
            <PropertyLocationPdfButton propertyId={propertyId} />
          </div>
        )}
      <div>
        {section === "info" && <Property propertyId={propertyId} />}
        {section === "location" && (
          <PropertyLocationReport propertyId={propertyId} />
        )}
        {section === "marketInsights" && (
          <PropertyMarketInsightsRoute propertyId={propertyId} />
        )}
        {section === "timeOnMarket" && (
          <SimilarPropertiesInsightsRoute propertyId={propertyId} />
        )}
      </div>
    </div>
  );
};
